/**
 * Cache Service
 * Provides intelligent caching with change detection and automatic invalidation
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  etag?: string;
  expiresAt: number;
}

interface CacheConfig {
  ttl?: number; // Time to live in milliseconds
  staleWhileRevalidate?: boolean; // Return stale data while fetching fresh
}

class CacheService {
  private static readonly CACHE_PREFIX = 'fitness_app_cache_';
  private static readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
  private static readonly STALE_TTL = 30 * 60 * 1000; // 30 minutes (stale but usable)

  /**
   * Generate cache key
   */
  private static getKey(key: string): string {
    return `${this.CACHE_PREFIX}${key}`;
  }

  /**
   * Get cached data
   */
  static get<T>(key: string): T | null {
    try {
      const cacheKey = this.getKey(key);
      const cached = localStorage.getItem(cacheKey);
      
      if (!cached) {
        return null;
      }

      const entry: CacheEntry<T> = JSON.parse(cached);
      const now = Date.now();

      // Check if expired
      if (now > entry.expiresAt) {
        // If stale but within stale TTL, return it but mark as stale
        if (now < entry.expiresAt + this.STALE_TTL) {
          return entry.data; // Return stale data
        }
        // Fully expired, remove it
        localStorage.removeItem(cacheKey);
        return null;
      }

      return entry.data;
    } catch (error) {
      console.error('Error reading from cache:', error);
      return null;
    }
  }

  /**
   * Check if data is stale (expired but still usable)
   */
  static isStale(key: string): boolean {
    try {
      const cacheKey = this.getKey(key);
      const cached = localStorage.getItem(cacheKey);
      
      if (!cached) {
        return false;
      }

      const entry: CacheEntry<any> = JSON.parse(cached);
      const now = Date.now();

      return now > entry.expiresAt && now < entry.expiresAt + this.STALE_TTL;
    } catch {
      return false;
    }
  }

  /**
   * Set cached data
   */
  static set<T>(key: string, data: T, config: CacheConfig = {}): void {
    try {
      const ttl = config.ttl || this.DEFAULT_TTL;
      const now = Date.now();
      
      const entry: CacheEntry<T> = {
        data,
        timestamp: now,
        expiresAt: now + ttl,
      };

      const cacheKey = this.getKey(key);
      localStorage.setItem(cacheKey, JSON.stringify(entry));
    } catch (error) {
      console.error('Error writing to cache:', error);
      // If storage is full, try to clear old entries
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        this.clearExpired();
        // Retry once
        try {
          const ttl = config.ttl || this.DEFAULT_TTL;
          const now = Date.now();
          const entry: CacheEntry<T> = {
            data,
            timestamp: now,
            expiresAt: now + ttl,
          };
          localStorage.setItem(this.getKey(key), JSON.stringify(entry));
        } catch (retryError) {
          console.error('Failed to cache after cleanup:', retryError);
        }
      }
    }
  }

  /**
   * Set cache with ETag for change detection
   */
  static setWithETag<T>(key: string, data: T, etag: string, config: CacheConfig = {}): void {
    try {
      const ttl = config.ttl || this.DEFAULT_TTL;
      const now = Date.now();
      
      const entry: CacheEntry<T> = {
        data,
        timestamp: now,
        etag,
        expiresAt: now + ttl,
      };

      const cacheKey = this.getKey(key);
      localStorage.setItem(cacheKey, JSON.stringify(entry));
    } catch (error) {
      console.error('Error writing to cache with ETag:', error);
    }
  }

  /**
   * Get ETag for a cache key
   */
  static getETag(key: string): string | null {
    try {
      const cacheKey = this.getKey(key);
      const cached = localStorage.getItem(cacheKey);
      
      if (!cached) {
        return null;
      }

      const entry: CacheEntry<any> = JSON.parse(cached);
      return entry.etag || null;
    } catch {
      return null;
    }
  }

  /**
   * Invalidate cache
   */
  static invalidate(key: string): void {
    try {
      const cacheKey = this.getKey(key);
      localStorage.removeItem(cacheKey);
      console.log('🗑️ Cache invalidated:', key, 'Removed key:', cacheKey);
    } catch (error) {
      console.error('Error invalidating cache:', error);
    }
  }

  /**
   * Invalidate all caches matching a pattern
   */
  static invalidatePattern(pattern: string): void {
    try {
      const keys = Object.keys(localStorage);
      const regex = new RegExp(pattern);
      
      keys.forEach(key => {
        if (key.startsWith(this.CACHE_PREFIX) && regex.test(key)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Error invalidating cache pattern:', error);
    }
  }

  /**
   * Clear all expired entries
   */
  static clearExpired(): void {
    try {
      const keys = Object.keys(localStorage);
      const now = Date.now();
      
      keys.forEach(key => {
        if (key.startsWith(this.CACHE_PREFIX)) {
          try {
            const cached = localStorage.getItem(key);
            if (cached) {
              const entry: CacheEntry<any> = JSON.parse(cached);
              // Remove if fully expired (past stale TTL)
              if (now > entry.expiresAt + this.STALE_TTL) {
                localStorage.removeItem(key);
              }
            }
          } catch {
            // Invalid entry, remove it
            localStorage.removeItem(key);
          }
        }
      });
    } catch (error) {
      console.error('Error clearing expired cache:', error);
    }
  }

  /**
   * Clear all cache
   */
  static clearAll(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.CACHE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Error clearing all cache:', error);
    }
  }

  /**
   * Get cache stats
   */
  static getStats(): { total: number; expired: number; valid: number } {
    try {
      const keys = Object.keys(localStorage);
      const now = Date.now();
      let total = 0;
      let expired = 0;
      let valid = 0;

      keys.forEach(key => {
        if (key.startsWith(this.CACHE_PREFIX)) {
          total++;
          try {
            const cached = localStorage.getItem(key);
            if (cached) {
              const entry: CacheEntry<any> = JSON.parse(cached);
              if (now > entry.expiresAt) {
                expired++;
              } else {
                valid++;
              }
            }
          } catch {
            expired++;
          }
        }
      });

      return { total, expired, valid };
    } catch {
      return { total: 0, expired: 0, valid: 0 };
    }
  }
}

export default CacheService;

